import { FC } from 'react'
import { MenuItem, Stack, TextField } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'

/**
 * Opções do filtro de período comum do Dashboard (L13, §0 da Especificação Definitiva:
 * "cada gráfico possui título, unidade, legenda e os mesmos filtros de período").
 * `TUDO` = sem recorte — a carteira inteira, exatamente o que o Pipeline mostra.
 */
export type OpcaoPeriodo = 'TUDO' | 'MES_CORRENTE' | 'MES_ANTERIOR' | 'TRIMESTRE' | 'ANO' | 'PERSONALIZADO'

export const OPCOES_PERIODO: { valor: OpcaoPeriodo; rotulo: string }[] = [
  { valor: 'TUDO', rotulo: 'Toda a carteira' },
  { valor: 'MES_CORRENTE', rotulo: 'Mês corrente' },
  { valor: 'MES_ANTERIOR', rotulo: 'Mês anterior' },
  { valor: 'TRIMESTRE', rotulo: 'Trimestre' },
  { valor: 'ANO', rotulo: 'Ano' },
  { valor: 'PERSONALIZADO', rotulo: 'Personalizado' },
]

/** Intervalo enviado ao backend (`inicio`/`fim`, ISO `YYYY-MM-DD`, inclusivos). */
export interface IntervaloPeriodo {
  inicio: string
  fim: string
}

export interface ValorFiltroPeriodo {
  opcao: OpcaoPeriodo
  /** Só no PERSONALIZADO: datas ISO digitadas. */
  inicio?: string
  fim?: string
}

const ISO = 'YYYY-MM-DD'

/**
 * Datas do período escolhido — calendário, não KPI: o recorte e todos os números são do
 * backend (`GET /v1/dashboard/resumo?inicio&fim`). Trimestre e ano são os CORRENTES (de
 * calendário). Devolve `null` para "Toda a carteira" e para um personalizado incompleto ou
 * invertido (a tela não consulta com período inválido).
 */
export function intervaloDoPeriodo(valor: ValorFiltroPeriodo, hoje: Dayjs = dayjs()): IntervaloPeriodo | null {
  const par = (a: Dayjs, b: Dayjs): IntervaloPeriodo => ({ inicio: a.format(ISO), fim: b.format(ISO) })
  switch (valor.opcao) {
    case 'TUDO':
      return null
    case 'MES_CORRENTE':
      return par(hoje.startOf('month'), hoje.endOf('month'))
    case 'MES_ANTERIOR': {
      const m = hoje.subtract(1, 'month')
      return par(m.startOf('month'), m.endOf('month'))
    }
    case 'TRIMESTRE': {
      const primeiroMes = Math.floor(hoje.month() / 3) * 3
      const inicio = hoje.month(primeiroMes).startOf('month')
      return par(inicio, inicio.add(2, 'month').endOf('month'))
    }
    case 'ANO':
      return par(hoje.startOf('year'), hoje.endOf('year'))
    case 'PERSONALIZADO': {
      if (!personalizadoValido(valor)) return null
      return { inicio: valor.inicio as string, fim: valor.fim as string }
    }
  }
}

/** Personalizado com as duas datas válidas e início ≤ fim. */
export function personalizadoValido(valor: ValorFiltroPeriodo): boolean {
  const isoValida = (d?: string) => !!d && /^\d{4}-\d{2}-\d{2}$/.test(d) && dayjs(d).isValid()
  if (!isoValida(valor.inicio) || !isoValida(valor.fim)) return false
  return !dayjs(valor.inicio).isAfter(dayjs(valor.fim))
}

/** Texto do período para títulos/legendas ("Mês corrente (01/10/2026 a 31/10/2026)"). */
export function rotuloDoPeriodo(valor: ValorFiltroPeriodo, hoje: Dayjs = dayjs()): string {
  const base = OPCOES_PERIODO.find((o) => o.valor === valor.opcao)?.rotulo ?? ''
  const intervalo = intervaloDoPeriodo(valor, hoje)
  if (!intervalo) return base
  const br = (iso: string) => dayjs(iso).format('DD/MM/YYYY')
  return `${base} (${br(intervalo.inicio)} a ${br(intervalo.fim)})`
}

export interface FiltroPeriodoProps {
  valor: ValorFiltroPeriodo
  onChange: (valor: ValorFiltroPeriodo) => void
}

/**
 * Seletor do período comum do Dashboard: um select (mês corrente, mês anterior, trimestre,
 * ano, personalizado — e "Toda a carteira") e, no personalizado, duas datas.
 */
export const FiltroPeriodo: FC<FiltroPeriodoProps> = ({ valor, onChange }) => {
  const personalizadoInvalido =
    valor.opcao === 'PERSONALIZADO' && !!valor.inicio && !!valor.fim && !personalizadoValido(valor)
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
      <TextField
        select
        size="small"
        label="Período"
        value={valor.opcao}
        onChange={(e) => onChange({ ...valor, opcao: e.target.value as OpcaoPeriodo })}
        sx={{ minWidth: 200 }}
      >
        {OPCOES_PERIODO.map((o) => (
          <MenuItem key={o.valor} value={o.valor}>
            {o.rotulo}
          </MenuItem>
        ))}
      </TextField>
      {valor.opcao === 'PERSONALIZADO' && (
        <>
          <TextField
            type="date"
            size="small"
            label="De"
            value={valor.inicio ?? ''}
            onChange={(e) => onChange({ ...valor, inicio: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Data inicial' }}
          />
          <TextField
            type="date"
            size="small"
            label="Até"
            value={valor.fim ?? ''}
            onChange={(e) => onChange({ ...valor, fim: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Data final' }}
            error={personalizadoInvalido}
            helperText={personalizadoInvalido ? 'A data final deve ser igual ou posterior à inicial' : undefined}
          />
        </>
      )}
    </Stack>
  )
}

export default FiltroPeriodo
